/**
 * /join + /join/success Client Bundle.
 *
 * WHAT THIS FILE DOES
 * - Boots Firebase Auth with the SSR-injected config (Google-only in v1).
 * - Drives every state inside #join-root: loading, signed-out, unpaid,
 *   beta-ended, paid, success, error.
 * - On `subscribe`, calls POST /api/billing/checkout, then redirects the
 *   tab to the hosted Stripe Checkout URL the backend returns.
 * - On `manage`, calls POST /api/billing/portal to open the Stripe
 *   Customer Portal.
 * - On /join/success, polls /api/auth/session until the webhook lands and
 *   `subscriptionActive` flips to true. Then surfaces the right next-step
 *   UI for web-first vs extension-first acquisitions.
 * - Sends the Firebase id token over the web auth bridge so an installed
 *   extension picks up the new session without a manual reload.
 *
 * WHY THIS EXISTS
 * - The plan calls for one canonical purchase path. /join is it. The
 *   client carries the entire state machine; the SSR shell only renders
 *   a frame and a mount point.
 *
 * NON-GOALS
 * - Embedding Stripe Elements (we use hosted Checkout — Phase 1 contract).
 * - Implementing email/password sign-in (deferred until verified-email
 *   flow lands).
 * - Polling for entitlement on /join itself; only /join/success polls.
 *
 * GROUND TRUTH CONTRACTS (must not be violated here)
 * - Account first, payment second: only an authenticated user with a
 *   verified email can hit /api/billing/checkout. The backend re-checks,
 *   but the client never offers the CTA to a signed-out user.
 * - Two prices only: the client POSTs `interval: "month" | "year"`. The
 *   backend resolves these against env Price IDs.
 * - Optional bootstrap `promo` (from `/join?promo=`) is POSTed with checkout
 *   so the backend can pre-apply a Stripe Promotion Code.
 * - One Checkout path: the client opens the URL the backend returns; it
 *   never builds Stripe URLs itself.
 *
 * RELATED FILES
 * - src/ssr/join.ts (SSR shell + bootstrap injection)
 * - src/ssr/join-success.ts (post-payment SSR shell, same mount)
 * - src/api/routes/billing.ts (Checkout / portal endpoints)
 * - src/api/routes/auth.ts (session payload carries `billing.subscriptionActive`)
 * - src/client/web-auth-bridge.ts (extension auth sync)
 * - src/client/join-render.ts (DOM rendering, extracted verbatim)
 *
 * @file src/client/join.ts
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  signOut,
  type Auth,
  type User
} from 'firebase/auth';
import { createWebAuthBridge, type WebAuthBridge, type WebAuthState } from './web-auth-bridge.js';
import { resolveDisplayIdentity, resolveUserEmailWithReload } from '@gist-protocol/core';
import {
  URL_PREFIX_CHECKOUT_SOURCE,
  type UrlPrefixCheckoutSource
} from '../lib/url-prefix-continuation.js';
import {
  resolveUrlPrefixJoinContext,
  type UrlPrefixJoinContext
} from './url-prefix-join.js';
import { clearWebWorkerSession, mintWebWorkerSession } from './web-session-cookie.js';
import {
  continueToUrlPrefix,
  createElement,
  renderAlreadyMember,
  renderError,
  renderLoading,
  renderSignedOut,
  renderSuccess,
  renderSuccessStillPending,
  renderUnpaid
} from './join-render.js';

/* ────────────────────────────────────────────────────────────────────────
   Public bootstrap shape (kept in sync with src/ssr/join.ts and join-success.ts)
   ──────────────────────────────────────────────────────────────────────── */

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
}

interface JoinBootstrap {
  source: 'web' | 'extension' | UrlPrefixCheckoutSource;
  /** Present on /join/success — flips into post-payment polling mode. */
  mode?: 'success';
  /** Stripe-supplied session id when arriving via /join/success. */
  sessionId?: string | null;
  /** True when /join was hit with ?canceled=1 (Checkout cancel redirect). */
  canceled?: boolean;
  /** Same-origin URL-prefix path to resume after payment. */
  continuePath?: string | null;
  /**
   * Customer-facing Stripe promotion code from `/join?promo=…`.
   * POSTed to /api/billing/checkout so Checkout can pre-apply the discount.
   */
  promo?: string | null;
}

/**
 * Injected by `src/ssr/join.ts` and `join-success.ts` so help/join links match
 * the current deployment origin (staging vs production).
 */
interface JoinPublic {
  origin: string;
  helpUrl: string;
  joinUrl: string;
  /** ISO instant: accounts created before this may see legacy beta copy. */
  betaCohortCutoffIso: string;
}

interface JoinServerUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  billing: BillingSnapshot | null;
}

declare global {
  interface Window {
    __GIST_JOIN_CONFIG__?: FirebaseConfig | null;
    __GIST_JOIN_BOOTSTRAP__?: JoinBootstrap | null;
    __GIST_JOIN_PUBLIC__?: JoinPublic | null;
    __GIST_JOIN_USER__?: JoinServerUser | null;
  }
}

/* ────────────────────────────────────────────────────────────────────────
   Constants — kept very small. SIMPLER IS BETTER.
   ──────────────────────────────────────────────────────────────────────── */

const CHECKOUT_URL = '/api/billing/checkout';
const PORTAL_URL = '/api/billing/portal';
const RECONCILE_CHECKOUT_URL = '/api/billing/reconcile-checkout';
const PROFILE_URL = '/api/profile';

/**
 * The join page runs in a normal browser tab (not the extension). `withVersionCheck`
 * requires `X-Gist-Protocol-Version` on most `/api/*` routes; without these headers,
 * requests return 426. (See `src/api/middleware/version-check.ts`.)
 *
 * Value `2` matches `ExtensionProtocolVersion.CURRENT` in `src/api/protocol-version.ts`.
 */
const JOIN_WEB_PROTOCOL_HEADERS: Record<string, string> = {
  'X-Gist-Protocol-Version': '2',
  'X-Gist-Client-Version': 'web-join'
};

/**
 * Polling cadence for /join/success.
 *
 * Webhooks usually land within a second or two. We poll up to ~30s before
 * surfacing a manual refresh because Stripe occasionally takes longer on
 * first-payment events. The user is never blocked: they can refresh by
 * hand at any point.
 */
const SuccessPolling = {
  INTERVAL_MS: 1500,
  MAX_ATTEMPTS: 20
} as const;

type Interval = 'month' | 'year';

export function shouldAttemptCheckoutReconciliation(args: {
  mode?: 'success';
  sessionId?: string | null;
  pollAttempts: number;
  reconcileAttempted: boolean;
  subscriptionActive: boolean;
}): boolean {
  return (
    args.mode === 'success' &&
    args.pollAttempts === 1 &&
    !args.reconcileAttempted &&
    !args.subscriptionActive &&
    typeof args.sessionId === 'string' &&
    args.sessionId.trim().length > 0
  );
}

export function hasConcreteJoinFirebaseUser(user: Pick<User, 'isAnonymous'> | null): boolean {
  return Boolean(user && !user.isAnonymous);
}

export function hasJoinPaidEntitlement(
  billing: {
    subscriptionActive?: boolean;
    entitlementActive?: boolean;
    isAdmin?: boolean | null;
  } | null | undefined,
  profile: { isAdmin?: boolean | null } | null | undefined
): boolean {
  return Boolean(billing?.entitlementActive || billing?.subscriptionActive || billing?.isAdmin || profile?.isAdmin);
}

interface StatusMessage {
  tone: 'error' | 'info' | 'success';
  text: string;
  portalPath?: string;
}

/**
 * Subset of GET /api/profile used for beta-ended vs standard unpaid copy.
 */
interface JoinProfileMeta {
  createdAt?: string;
  isBeta?: boolean;
  isAdmin?: boolean;
  /** Mirrors Firestore / GET /profile — used when session billing is still null. */
  stripeCustomerId?: string | null;
}

interface BillingSnapshot {
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: string | null;
  subscriptionTrialEnd: string | null;
  subscribedAt: string | null;
  subscriptionActive: boolean;
  entitlementActive?: boolean;
  isAdmin?: boolean | null;
}

interface SessionResponse {
  token: string;
  user: { uid: string; email: string | null; emailVerified: boolean };
  billing: BillingSnapshot | null;
}

/* ────────────────────────────────────────────────────────────────────────
   JoinController — single owner of #join-root.

   Several members are intentionally public (not `private`) because the
   extracted renderers in join-render.ts call back through the instance.
   ──────────────────────────────────────────────────────────────────────── */

export class JoinController {
  root: HTMLElement;
  private app: FirebaseApp | null = null;
  auth: Auth | null = null;
  user: User | null = null;
  billing: BillingSnapshot | null = null;
  /** Loaded from GET /api/profile after sign-in (beta-ended heuristic). */
  profileMeta: JoinProfileMeta | null = null;
  /**
   * True after the first post-sign-in session + profile fetch completes (or user signed out).
   * Avoids flashing unpaid UI before billing/profile data exists.
   */
  private entitlementHydrated = false;
  private bridge: WebAuthBridge;
  bootstrap: JoinBootstrap;
  busy = false;
  currentInterval: Interval = 'month';
  successPollAttempts = 0;
  successPollTimer: number | null = null;
  private successReconcileAttempted = false;
  continuationStarted = false;
  /**
   * Auth state delivered by the extension over the gist-auth-state bridge.
   *
   * The extension fans out its worker session + identity to gist.is pages so
   * users who signed in inside the extension don't see a signed-out join
   * page when the gist.is origin has no Firebase Auth persistence (e.g. fresh
   * tab opened via `chrome.tabs.create`, or IndexedDB cleared by ITP/Brave).
   *
   * When set and Firebase has no user, the join page treats this as a valid
   * signed-in identity and uses `workerToken` as the Authorization Bearer for
   * /api/billing/checkout, /api/profile, etc. (Backend `requireAuth` accepts
   * both Firebase id tokens and worker session tokens — see lib/auth.ts.)
   */
  private bridgeAuth: WebAuthState | null = null;
  private serverIdentity: JoinServerUser | null = null;
  statusMessage: StatusMessage | null = null;

  constructor(root: HTMLElement, bootstrap: JoinBootstrap) {
    this.root = root;
    this.bootstrap = bootstrap;
    this.bridge = createWebAuthBridge({
      onAuthState: (state) => {
        const previousToken = this.bridgeAuth?.workerToken ?? null;
        this.bridgeAuth = state;
        // When the bridge delivers a new identity and Firebase has no user,
        // refresh billing+profile using the worker token and re-render so
        // the page reflects "signed in via extension" instead of the
        // signed-out CTA. Firebase-user paths already drive this via
        // onAuthStateChanged.
        if (!this.hasConcreteFirebaseUser() && state?.workerToken && state.workerToken !== previousToken) {
          this.entitlementHydrated = false;
          this.render();
          void this.refreshUserStateAfterSignIn();
        } else if (!state && !this.hasConcreteFirebaseUser()) {
          // Extension signed out and we never had a Firebase user — drop to
          // the signed-out CTA immediately.
          this.billing = null;
          this.profileMeta = null;
          this.entitlementHydrated = true;
          this.render();
        }
      }
    });
  }

  /**
   * Boot Firebase, mount the right initial state, and start observing auth.
   */
  async init(): Promise<void> {
    const config = window.__GIST_JOIN_CONFIG__ ?? null;
    this.serverIdentity = window.__GIST_JOIN_USER__ ?? null;
    if (this.serverIdentity) {
      this.billing = this.serverIdentity.billing ?? null;
      this.entitlementHydrated = true;
    }
    if (!config) {
      this.statusMessage = {
        tone: 'error',
        text: 'Sign-up is briefly unavailable. Please try again in a moment.'
      };
      renderError(this);
      return;
    }

    if (this.bootstrap.canceled) {
      this.statusMessage = {
        tone: 'info',
        text: "Checkout was canceled. Your plan is still here whenever you’re ready."
      };
    }

    try {
      this.app = initializeApp(config);
      this.auth = getAuth(this.app);
      this.bridge.listen();
      if (this.serverIdentity) {
        this.render();
      }

      onAuthStateChanged(this.auth, (user) => {
        const previousUid = this.hasConcreteFirebaseUser() ? this.user?.uid ?? null : null;
        this.user = user;
        const hasFirebaseIdentity = this.hasConcreteFirebaseUser();
        if (hasFirebaseIdentity && user && this.serverIdentity && user.uid !== this.serverIdentity.uid) {
          console.warn('[gist-join] Firebase user differs from server cookie identity; keeping server cookie identity', {
            firebaseUid: user.uid,
            serverUid: this.serverIdentity.uid
          });
          this.billing = this.serverIdentity.billing ?? null;
          this.entitlementHydrated = true;
          void this.refreshProfileMeta();
          this.render();
          return;
        }
        if (!hasFirebaseIdentity) {
          // Firebase has no concrete user. Server cookie identity or bridge identity
          // keeps the page signed in; anonymous Firebase must not win checkout auth.
          if (this.serverIdentity) {
            this.billing = this.serverIdentity.billing ?? null;
            this.entitlementHydrated = true;
          } else {
            this.billing = null;
            this.profileMeta = null;
          }
          if (!this.serverIdentity && this.bridgeAuth?.workerToken) {
            // Let the bridge-driven refresh path own entitlement hydration.
            this.entitlementHydrated = false;
            void this.refreshUserStateAfterSignIn();
          } else if (!this.serverIdentity) {
            this.entitlementHydrated = true;
          }
        } else if (user && user.uid !== previousUid) {
          if (!this.serverIdentity) {
            this.billing = null;
            this.profileMeta = null;
            this.entitlementHydrated = false;
            void this.refreshUserStateAfterSignIn();
            void this.syncAuthToExtension(user);
          }
          this.successReconcileAttempted = false;
        }
        this.render();
      });
    } catch (error) {
      console.error('[gist-join] Firebase init failed', error);
      this.statusMessage = {
        tone: 'error',
        text: 'Sign-up is briefly unavailable. Please try again in a moment.'
      };
      renderError(this);
    }
  }

  /* ──────────────── Auth ↔ Extension bridging ──────────────── */

  private async syncAuthToExtension(user: User): Promise<void> {
    if (user.isAnonymous) {
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const resolvedEmail = await resolveUserEmailWithReload(user);
      this.bridge.sendAuthImport({ idToken, email: resolvedEmail });
    } catch (error) {
      console.warn('[gist-join] Failed to sync web auth to extension', error);
    }
  }

  /* ──────────────── Backend session ──────────────── */

  /**
   * Resolves the Authorization header to use for authenticated API calls.
   *
   * Prefers a fresh Firebase id token when a Firebase user is present (the
   * primary path). Falls back to the extension-supplied worker token when
   * only the bridge has authenticated the page. Backend `requireAuth`
   * accepts both — worker tokens are prefixed `gist-worker.`.
   *
   * @returns `Bearer <token>` value, or null when no auth is available.
   */
  private async getAuthorizationHeader(): Promise<string | null> {
    if (this.serverIdentity && this.user && !this.user.isAnonymous && this.user.uid !== this.serverIdentity.uid) {
      // The HttpOnly cookie is what SSR and same-origin APIs authenticate as.
      // A stale Firebase user from IndexedDB must not override it with Bearer.
      return null;
    }
    if (this.user && !this.user.isAnonymous) {
      try {
        const idToken = await this.user.getIdToken();
        return `Bearer ${idToken}`;
      } catch (error) {
        console.warn('[gist-join] getIdToken failed; falling back to bridge token if present', error);
      }
    }
    if (this.bridgeAuth?.workerToken && this.bridgeMatchesServerIdentity()) {
      return `Bearer ${this.bridgeAuth.workerToken}`;
    }
    return null;
  }

  private bridgeMatchesServerIdentity(): boolean {
    if (!this.serverIdentity || !this.bridgeAuth) {
      return true;
    }
    const profile = this.bridgeAuth.profile;
    const bridgeUid = typeof profile?.uid === 'string'
      ? profile.uid
      : typeof profile?.userId === 'string'
        ? profile.userId
        : null;
    if (bridgeUid) {
      return bridgeUid === this.serverIdentity.uid;
    }
    return Boolean(this.bridgeAuth.email && this.serverIdentity.email && this.bridgeAuth.email === this.serverIdentity.email);
  }

  private async buildAuthenticatedHeaders(contentTypeJson = false): Promise<HeadersInit | null> {
    const auth = await this.getAuthorizationHeader();
    if (!auth && !this.serverIdentity) {
      return null;
    }
    return {
      ...(auth ? { Authorization: auth } : {}),
      ...(contentTypeJson ? { 'Content-Type': 'application/json' } : {}),
      ...JOIN_WEB_PROTOCOL_HEADERS
    };
  }

  /**
   * Email shown in the "Signed in as" row. Centralized in
   * `resolveDisplayIdentity` — returns `null` (never a UID) when no source
   * carries an email; callers render `'Signed in'` instead.
   */
  getDisplayEmail(): string | null {
    return resolveDisplayIdentity({
      serverIdentity: this.serverIdentity,
      firebaseUser: this.user && !this.user.isAnonymous ? this.user : null,
      bridgeState: this.bridgeAuth
    });
  }

  /** True when either Firebase or the extension bridge has authenticated the page. */
  hasIdentity(): boolean {
    return Boolean(this.serverIdentity || this.hasConcreteFirebaseUser() || this.bridgeAuth?.workerToken);
  }

  hasPaidEntitlement(billing: BillingSnapshot | null = this.billing): boolean {
    return hasJoinPaidEntitlement(billing, this.profileMeta);
  }

  private hasConcreteFirebaseUser(): boolean {
    return hasConcreteJoinFirebaseUser(this.user);
  }

  /**
   * Exchanges the Firebase id token for a worker session and stores the
   * billing snapshot returned alongside it. The session payload is the
   * single read path for billing state on the web (see plan: "One billing
   * read path").
   */
  async refreshSessionAndBilling(): Promise<BillingSnapshot | null> {
    const auth = await this.getAuthorizationHeader();
    if (!auth && !this.serverIdentity) {
      this.billing = null;
      return null;
    }
    try {
      const body = await mintWebWorkerSession<SessionResponse>({
        authorization: auth,
        clientVersion: 'web-join',
        logPrefix: '[gist-join]'
      });
      if (!body) {
        return this.billing;
      }
      this.billing = body?.billing ?? null;
      return this.billing;
    } catch (error) {
      console.warn('[gist-join] Session refresh threw', error);
      return this.billing;
    }
  }

  /**
   * Loads profile fields needed for beta-ended copy (`isBeta`, `createdAt`).
   *
   * Called by:
   * - `refreshUserStateAfterSignIn()` alongside session refresh
   */
  private async refreshProfileMeta(): Promise<void> {
    const headers = await this.buildAuthenticatedHeaders();
    if (!headers) {
      this.profileMeta = null;
      return;
    }
    try {
      const response = await fetch(PROFILE_URL, {
        credentials: 'include',
        headers
      });
      if (!response.ok) {
        return;
      }
      const body = (await response.json()) as Record<string, unknown>;
      const createdAt = typeof body.createdAt === 'string' ? body.createdAt : undefined;
      const stripeCustomerId =
        typeof body.stripeCustomerId === 'string' ? body.stripeCustomerId : null;
      this.profileMeta = {
        createdAt,
        isBeta: body.isBeta === true,
        isAdmin: body.isAdmin === true,
        stripeCustomerId
      };
    } catch (error) {
      console.warn('[gist-join] Profile refresh threw', error);
    }
  }

  /**
   * Refreshes billing + profile after a new sign-in, then re-renders so unpaid
   * state reflects server data (avoids stale `billing` on the first paint).
   */
  private async refreshUserStateAfterSignIn(): Promise<void> {
    try {
      await Promise.all([this.refreshSessionAndBilling(), this.refreshProfileMeta()]);
    } finally {
      this.entitlementHydrated = true;
      this.render();
    }
  }

  /* ──────────────── Sign-in ──────────────── */

  async handleGoogleSignIn(): Promise<void> {
    if (!this.auth || this.busy) return;
    this.busy = true;
    this.statusMessage = null;
    this.render();

    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      try {
        await signInWithPopup(this.auth, provider);
      } catch (popupError) {
        const code = (popupError as { code?: string })?.code ?? '';
        // Some browsers (Brave, embedded views) block popups outright.
        // Fall back to a full-page redirect so the user is never stuck.
        if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
          await signInWithRedirect(this.auth, provider);
          return;
        }
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          // The user dismissed the popup; treat that as a no-op.
          return;
        }
        throw popupError;
      }
    } catch (error) {
      console.error('[gist-join] Google sign-in failed', error);
      this.statusMessage = {
        tone: 'error',
        text: 'Sign in failed. Please try again.'
      };
    } finally {
      this.busy = false;
      this.render();
    }
  }

  /* ──────────────── Checkout ──────────────── */

  async handleCheckout(): Promise<void> {
    if (!this.hasIdentity() || this.busy) return;
    const headers = await this.buildAuthenticatedHeaders(true);
    if (!headers) return;
    this.busy = true;
    this.statusMessage = null;
    this.render();

    try {
      const response = await fetch(CHECKOUT_URL, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          interval: this.currentInterval,
          source: this.bootstrap.source,
          continuePath: this.resolveContinuePath(),
          ...(this.bootstrap.promo ? { promo: this.bootstrap.promo } : {})
        })
      });

      const body = (await response.json().catch(() => null)) as
        | { url?: string; error?: string; portalPath?: string }
        | null;

      if (!response.ok || !body?.url) {
        const errorCode = body?.error ?? 'checkout_failed';
        if (errorCode === 'already_subscribed' && this.bootstrap.source === URL_PREFIX_CHECKOUT_SOURCE) {
          this.busy = false;
          continueToUrlPrefix(this);
          return;
        }
        const portalPath =
          (errorCode === 'already_subscribed' || errorCode === 'payment_recovery_required')
          && typeof body?.portalPath === 'string'
            ? body.portalPath
            : undefined;
        this.statusMessage = {
          tone: 'error',
          text: this.resolveCheckoutErrorMessage(errorCode),
          portalPath
        };
        this.busy = false;
        this.render();
        return;
      }

      // Hand the tab to Stripe. Do not flip busy back; the page is leaving.
      window.location.assign(body.url);
    } catch (error) {
      console.error('[gist-join] Checkout request failed', error);
      this.statusMessage = {
        tone: 'error',
        text: "We couldn’t reach the payment system. Check your connection and try again."
      };
      this.busy = false;
      this.render();
    }
  }

  private resolveCheckoutErrorMessage(code: string): string {
    switch (code) {
      case 'email_unverified':
        return 'Please verify your Google email before you subscribe.';
      case 'missing_email':
        return 'Your account is missing an email address. Sign in again to fix it.';
      case 'invalid_price':
        return 'That plan isn’t available right now. Reload and try again.';
      case 'billing_unavailable':
        return 'Checkout is briefly unavailable. Please try again in a moment.';
      case 'already_subscribed':
        return "You're already subscribed — manage your plan from the Portal.";
      case 'payment_recovery_required':
        return 'Your subscription needs attention — update payment details in the Portal.';
      case 'checkout_conflict':
        return 'A trial checkout already started with your first choices. Continue with those choices and try again.';
      default:
        return 'We couldn’t start checkout. Please try again.';
    }
  }

  /* ──────────────── Manage (Stripe Customer Portal) ──────────────── */

  async handleManage(portalPath: string = PORTAL_URL): Promise<void> {
    if (!this.hasIdentity() || this.busy) return;
    const headers = await this.buildAuthenticatedHeaders(true);
    if (!headers) return;
    this.busy = true;
    this.statusMessage = null;
    this.render();

    try {
      const response = await fetch(portalPath, {
        method: 'POST',
        credentials: 'include',
        headers
      });
      const body = (await response.json().catch(() => null)) as
        | { url?: string; error?: string }
        | null;

      if (response.status === 400 && body && typeof body === 'object') {
        const err = body as { error?: string; checkoutUrl?: string };
        if (err.error === 'no_stripe_customer' && typeof err.checkoutUrl === 'string') {
          window.location.assign(err.checkoutUrl);
          return;
        }
      }

      if (!response.ok || !body?.url) {
        this.statusMessage = {
          tone: 'error',
          text: 'We couldn’t open your billing page. Please try again.'
        };
        this.busy = false;
        this.render();
        return;
      }

      window.location.assign(body.url);
    } catch (error) {
      console.error('[gist-join] Portal request failed', error);
      this.statusMessage = {
        tone: 'error',
        text: 'We couldn’t open your billing page. Please try again.'
      };
      this.busy = false;
      this.render();
    }
  }

  /* ──────────────── Sign out ──────────────── */

  async handleSignOut(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.render();
    try {
      const auth = await this.getAuthorizationHeader();
      await clearWebWorkerSession({
        authorization: auth,
        clientVersion: 'web-join',
        logPrefix: '[gist-join]'
      });
      if (this.auth && this.user) {
        await signOut(this.auth);
      }
      // Always tell the extension to drop its session. For bridge-only
      // identity (no Firebase user) this is the only thing that actually
      // signs the user out.
      this.bridge.sendLogout();
      this.bridgeAuth = null;
      this.serverIdentity = null;
      this.billing = null;
      this.profileMeta = null;
      this.entitlementHydrated = true;
    } catch (error) {
      console.warn('[gist-join] Sign-out failed', error);
    } finally {
      this.busy = false;
      this.render();
    }
  }

  /* ──────────────── Rendering ──────────────── */

  render(): void {
    // Success-mode pages own their own polling loop.
    if (this.bootstrap.mode === 'success') {
      renderSuccess(this);
      return;
    }

    if (!this.auth) {
      renderLoading(this);
      return;
    }

    // Server cookie identity, Firebase, or the extension bridge count as signed in.
    if (!this.hasIdentity()) {
      renderSignedOut(this);
      return;
    }

    if (!this.entitlementHydrated) {
      renderLoading(this);
      return;
    }

    if (this.hasPaidEntitlement()) {
      renderAlreadyMember(this);
      return;
    }

    renderUnpaid(this);
  }

  /* ──────────────── Success polling ──────────────── */

  startSuccessPoll(): void {
    this.stopSuccessPoll();
    const tick = async () => {
      this.successPollAttempts += 1;
      const billing = await this.refreshSessionAndBilling();
      if (this.hasPaidEntitlement(billing)) {
        this.stopSuccessPoll();
        // Re-import the (now-paid) auth state into any installed extension
        // so an already-open options page can pick up the entitlement
        // change without a manual reload. The extension's web auth import
        // path re-fetches the profile and broadcasts AUTH_STATE_CHANGED,
        // which the options UI listens for.
        if (this.user) {
          void this.syncAuthToExtension(this.user);
        }
        this.render();
        return;
      }
      const successSessionId =
        typeof this.bootstrap.sessionId === 'string' ? this.bootstrap.sessionId.trim() : '';
      if (
        shouldAttemptCheckoutReconciliation({
          mode: this.bootstrap.mode,
          sessionId: successSessionId,
          pollAttempts: this.successPollAttempts,
          reconcileAttempted: this.successReconcileAttempted,
          subscriptionActive: this.hasPaidEntitlement(billing)
        })
      ) {
        await this.reconcileCheckoutOnce(successSessionId);
        const reconciledBilling = await this.refreshSessionAndBilling();
        if (this.hasPaidEntitlement(reconciledBilling)) {
          this.stopSuccessPoll();
          if (this.user) {
            void this.syncAuthToExtension(this.user);
          }
          this.render();
          return;
        }
      }
      if (this.successPollAttempts >= SuccessPolling.MAX_ATTEMPTS) {
        this.stopSuccessPoll();
        this.renderSuccessStillPending();
        return;
      }
      this.successPollTimer = window.setTimeout(() => {
        void tick();
      }, SuccessPolling.INTERVAL_MS);
    };
    this.successPollTimer = window.setTimeout(() => {
      void tick();
    }, SuccessPolling.INTERVAL_MS);
  }

  private async reconcileCheckoutOnce(sessionId: string): Promise<void> {
    if (!this.hasIdentity() || this.successReconcileAttempted) return;
    const auth = await this.getAuthorizationHeader();
    if (!auth) return;
    this.successReconcileAttempted = true;
    try {
      await fetch(RECONCILE_CHECKOUT_URL, {
        method: 'POST',
        headers: {
          Authorization: auth,
          'Content-Type': 'application/json',
          ...JOIN_WEB_PROTOCOL_HEADERS
        },
        body: JSON.stringify({ sessionId })
      });
    } catch (error) {
      console.warn('[gist-join] Checkout reconciliation threw', error);
    }
  }

  private stopSuccessPoll(): void {
    if (this.successPollTimer !== null) {
      window.clearTimeout(this.successPollTimer);
      this.successPollTimer = null;
    }
  }

  private renderSuccessStillPending(): void {
    renderSuccessStillPending(this);
  }

  /**
   * Renders the status line; stays in this file because the "Open Portal"
   * affordance is part of the billing-action contract guarded by
   * tests/client/join-billing-hardening.test.ts.
   */
  renderStatus(): HTMLElement {
    if (!this.statusMessage) {
      return createElement('p', { class: 'join-status', 'aria-hidden': 'true' });
    }
    const cls =
      this.statusMessage.tone === 'error'
        ? 'join-status join-status--error'
        : this.statusMessage.tone === 'success'
        ? 'join-status join-status--success'
        : 'join-status join-status--info';
    const status = createElement('p', { class: cls, role: 'status' }, this.statusMessage.text);
    if (this.statusMessage.portalPath) {
      const button = createElement(
        'button',
        { type: 'button', class: 'join-status-action', disabled: this.busy },
        'Open Portal'
      );
      button.addEventListener('click', () => {
        if (this.statusMessage?.portalPath) {
          void this.handleManage(this.statusMessage.portalPath);
        }
      });
      status.appendChild(document.createTextNode(' '));
      status.appendChild(button);
    }
    return status;
  }

  resolveContinuePath(): string | null {
    return this.resolveUrlPrefixContext()?.continuePath ?? null;
  }

  resolveUrlPrefixContext(): UrlPrefixJoinContext | null {
    return resolveUrlPrefixJoinContext(this.bootstrap);
  }
}

/* ────────────────────────────────────────────────────────────────────────
   Bootstrap
   ──────────────────────────────────────────────────────────────────────── */

function bootstrap(): void {
  const root = document.getElementById('join-root');
  if (!root) {
    console.error('[gist-join] Mount point #join-root missing');
    return;
  }
  const bootstrapBlob = window.__GIST_JOIN_BOOTSTRAP__ ?? { source: 'web' as const };
  const controller = new JoinController(root, bootstrapBlob);
  void controller.init();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
}
